const MEANINGFUL = new Set([
  "SECTION", "MAIN", "HEADER", "NAV", "FOOTER", "H1", "H2", "H3", "H4",
  "P", "BLOCKQUOTE", "IMG", "BUTTON", "A", "FIGURE", "FORM", "UL",
]);

export const DEVICES = ["desktop", "tablet", "mobile"];
export const DEVICE_WIDTHS = { desktop: "100%", tablet: "768px", mobile: "390px" };

export const emptyOverrides = () => ({ elements: {}, moves: [], clones: [] });

export const normalizeOverrides = (ov) => {
  const o = emptyOverrides();
  if (ov && typeof ov === "object") {
    o.elements = ov.elements || {};
    o.moves = ov.moves || [];
    o.clones = ov.clones || [];
  }
  return o;
};

export const cloneOv = (ov) => JSON.parse(JSON.stringify(ov));

export function inferType(el) {
  const t = el.tagName;
  if (el.id === "public-root") return "page";
  if (t === "SECTION") return "section";
  if (t === "HEADER" || t === "NAV") return "navigation";
  if (t === "FOOTER") return "footer";
  if (t === "MAIN") return "container";
  if (/^H[1-4]$/.test(t)) return "heading";
  if (t === "P" || t === "BLOCKQUOTE") return "text";
  if (t === "IMG") return "image";
  if (t === "BUTTON") return "button";
  if (t === "A") return "link";
  if (t === "FORM") return "form";
  if (t === "FIGURE") return "card";
  return "container";
}

function hashPath(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function autoId(el) {
  if (el.dataset.testid) return el.dataset.testid;
  const parts = [];
  let cur = el;
  while (cur && cur.id !== "public-root" && cur !== document.body && !cur.dataset?.builderUi) {
    const parent = cur.parentElement;
    if (!parent) break;
    parts.unshift(cur.tagName.toLowerCase() + Array.prototype.indexOf.call(parent.children, cur));
    cur = parent;
  }
  return "e" + hashPath(parts.join("/"));
}

function isMeaningful(el) {
  if (!(el instanceof Element)) return false;
  if (el.closest("[data-builder-ui]")) return false;
  if (el.dataset.testid) return true;
  return MEANINGFUL.has(el.tagName);
}

export function ensureEids(root) {
  if (!root) return;
  const page = root.id === "public-root" ? root : root.querySelector("#public-root");
  if (page && !page.dataset.eid) {
    page.dataset.eid = "page";
    page.dataset.etype = "page";
  }
  for (const el of [root, ...root.querySelectorAll("*")]) {
    if (el.dataset.eid || !isMeaningful(el)) continue;
    el.dataset.eid = autoId(el);
    el.dataset.etype = inferType(el);
  }
  for (const el of root.querySelectorAll("[data-eid]")) {
    let p = el.parentElement;
    while (p && p !== root && p.id !== "public-root" && !p.dataset.eid && !p.closest("[data-builder-ui]")) {
      p.dataset.eid = autoId(p);
      p.dataset.etype = "container";
      p = p.parentElement;
    }
  }
}

export function findEditable(target, root) {
  let el = target instanceof Element ? target : null;
  while (el && el !== document.body) {
    if (el.dataset?.eid) return el;
    if (el === root) break;
    el = el.parentElement;
  }
  return null;
}

export function parentEidOf(parentEl) {
  let p = parentEl;
  while (p) {
    if (p.dataset?.eid) return p.dataset.eid;
    p = p.parentElement;
  }
  return "page";
}

export function getLabel(el) {
  const t = el.dataset.etype || "element";
  const txt = (el.innerText || el.getAttribute("alt") || "").trim().replace(/\s+/g, " ");
  return txt ? `${t} · ${txt.slice(0, 26)}` : `${t} · ${el.dataset.eid}`;
}

const kebab = (s) => s.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase());

export function buildCss(ov, mode) {
  let out = "";
  const ruleFor = (eid, styles, device) => {
    const decl = Object.entries(styles || {})
      .filter(([, v]) => v !== "" && v != null)
      .map(([k, v]) => `${kebab(k)}:${v} !important`)
      .join(";");
    if (!decl) return "";
    if (mode === "builder") return `[data-vp="${device}"] [data-eid="${eid}"]{${decl}}`;
    if (device === "desktop") return `[data-eid="${eid}"]{${decl}}`;
    return `@media(max-width:${device === "tablet" ? "1023px" : "767px"}){[data-eid="${eid}"]{${decl}}}`;
  };
  for (const [eid, cfg] of Object.entries(ov.elements || {})) {
    if (cfg.hidden) out += `[data-eid="${eid}"]{display:none !important}`;
    for (const d of DEVICES) out += ruleFor(eid, (cfg.styles || {})[d], d);
  }
  return out;
}

export function applyOverrides(root, ov, mode) {
  if (!root || !ov) return;
  ensureEids(root);

  for (const c of ov.clones || []) {
    const base = root.querySelector(`[data-eid="${CSS.escape(c.base)}"]`);
    if (!base || root.querySelector(`[data-eid="${CSS.escape(c.eid)}"]`)) continue;
    const clone = base.cloneNode(true);
    clone.dataset.eid = c.eid;
    base.parentElement.insertBefore(clone, base.nextSibling);
  }

  for (const m of ov.moves || []) {
    const el = root.querySelector(`[data-eid="${CSS.escape(m.eid)}"]`);
    const parent = root.querySelector(`[data-eid="${CSS.escape(m.parent)}"]`);
    if (!el || !parent) continue;
    const sibs = Array.from(parent.querySelectorAll(":scope > [data-eid]"));
    if (sibs[m.index] === el) continue;
    parent.insertBefore(el, sibs[m.index] || null);
  }

  for (const [eid, cfg] of Object.entries(ov.elements || {})) {
    const el = root.querySelector(`[data-eid="${CSS.escape(eid)}"]`);
    if (!el) continue;
    if (cfg.deleted) {
      el.remove();
      continue;
    }
    if (cfg.text != null && root.dataset.editingEid !== eid) el.innerText = cfg.text;
    if (cfg.href && el.tagName === "A") el.setAttribute("href", cfg.href);
    if (cfg.src && el.tagName === "IMG") el.setAttribute("src", cfg.src);
    if (cfg.alt && el.tagName === "IMG") el.setAttribute("alt", cfg.alt);
  }

  let tag = document.getElementById("visual-overrides");
  if (!tag) {
    tag = document.createElement("style");
    tag.id = "visual-overrides";
    document.head.appendChild(tag);
  }
  tag.textContent = buildCss(ov, mode);
}

export function setStyle(ov, eid, device, prop, value) {
  const n = cloneOv(ov);
  n.elements[eid] = n.elements[eid] || { styles: {} };
  n.elements[eid].styles = n.elements[eid].styles || {};
  n.elements[eid].styles[device] = { ...(n.elements[eid].styles[device] || {}), [prop]: value };
  return n;
}

export function setProp(ov, eid, key, value) {
  const n = cloneOv(ov);
  n.elements[eid] = { ...(n.elements[eid] || {}), [key]: value };
  return n;
}

export function addMove(ov, eid, parent, index) {
  const n = cloneOv(ov);
  n.moves = (n.moves || []).filter((m) => m.eid !== eid);
  n.moves.push({ eid, parent, index });
  return n;
}

export function addClone(ov, base, eid) {
  const n = cloneOv(ov);
  n.clones = [...(n.clones || []), { base, eid }];
  return n;
}

export function resolvedStyle(ov, eid, device, prop) {
  const s = (ov.elements?.[eid]?.styles) || {};
  if (s[device]?.[prop] != null && s[device]?.[prop] !== "") return s[device][prop];
  if (device !== "desktop" && s.desktop?.[prop] != null) return s.desktop[prop];
  return "";
}
